import {
  Box,
  Divider,
  HStack,
  ListItem,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { ListUtils } from '../../framework/utils/ListUtils';
import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { useColorContext } from '../containers/ColorProvider';
import { useSizeContext } from '../containers/SizeProvider';
import { useGlobalStateMachine } from '../GlobalStore';
import { useEngeneNavigate } from '../hooks/navigate';
import { DeadlineCategory } from '../models/gen/deadlineCategory';
import { DeadlineSearchResultResults } from '../models/gen/deadlineSearchResultResults';
import { HalfPeriod } from '../models/gen/halfPeriod';
import { Notice } from '../models/gen/notice';
import { ToDoWarning } from '../models/gen/toDoWarning';
import DeadlineUtils, { MessageClickOptions } from '../utils/DeadlineUtils';
import { Formatter } from '../utils/FormatUtils';
import StyledList from './StyledList';

type StyledMessageListProps = {
  hasLink?: boolean;
  // isWarning?: boolean;
  title?: string;
  array: Array<DeadlineSearchResultResults | Notice>;
  onOpen?: Consumer<number>;
};

/**
 *
 * @param title - メッセージタイトル
 * @param array - メッセージ一覧
 * @param hasLink - メッセージを押したときに画面遷移するかどうか
 * @returns
 */
const StyledMessageList: React.FunctionComponent<StyledMessageListProps> = ({
  title,
  array,
  hasLink = true,
  onOpen,
  ...props
}) => {
  const { state } = useGlobalStateMachine();
  const sizes = useSizeContext();

  const sorted = React.useMemo(
    () =>
      array.sort(
        ListUtils.sort((m) =>
          DeadlineUtils.isDeadline(m)
            ? m.ToDoWarnings![m.ToDoWarnings!.length - 1]?.limitTime
            : undefined
        )
      ),
    [array]
  );

  const items = React.useMemo(
    () =>
      sorted
        .map((message, idx0) => (
          <DeadlineItem
            hasLink={hasLink}
            key={idx0}
            index={idx0}
            message={message}
            onOpen={onOpen}
          />
        ))
        .flat(),
    [sorted, state.userInfo]
  );

  return (
    <StyledList
      {...props}
      isMessage
      header={title}
      items={items}
      border="none"
      width={sizes.width.styledMessageList}
    />
  );
};

type DeadlineItemProps = Pick<StyledMessageListProps, 'hasLink' | 'onOpen'> & {
  key: React.Key;
  index: number;
  message: DeadlineSearchResultResults | Notice;
};

const DeadlineItem = React.memo<DeadlineItemProps>(
  ({ message, hasLink, index, onOpen }: DeadlineItemProps) => {
    const { state } = useGlobalStateMachine();
    const navigate = useEngeneNavigate();
    const location = useLocation();
    const colors = useColorContext();
    const sizes = useSizeContext();
    const bodyDirection = useBreakpointValue({ base: 'flex', lg: 'grid' });
    // const hasWarning = message.deadlines
    //   .map((d) => d.type)
    //   .some((t) => t === 'warning' || t === 'delayed');
    const options = React.useMemo<MessageClickOptions>(
      () => ({
        matterIndex: DeadlineUtils.isWorkReport(message)
          ? message.matterIndex
          : undefined,
        onOpen,
        outsourcerCode: state.userInfo?.outsourcer_code,
        userCode: DeadlineUtils.isWorkReport(message)
          ? message.userCode || state.userInfo?.user_code
          : undefined,
      }),
      [message, state.userInfo]
    );
    const canClick =
      `${location.pathname}${location.search}` ===
      DeadlineUtils.generateUrl(message, options)
        ? false
        : DeadlineUtils.isAbleToClick(message);
    const isDeadline = DeadlineUtils.isDeadline(message);
    const category = isDeadline ? message.category : DeadlineCategory.Notice;

    return (
      <>
        <ListItem
          key={index}
          onClick={
            hasLink && canClick
              ? DeadlineUtils.onClick(message, navigate, options)
              : undefined
          }
          cursor={canClick ? 'pointer' : undefined}
          borderRadius={canClick ? '2xl' : undefined}
          boxShadow={canClick ? '4px 3px 5px 0px rgba(0,0,0,0.35)' : undefined}
          backgroundColor={colors.messageBackground.get(category)}
          // color="white"
          paddingX={4}
          paddingY={2}
        >
          <Box
            // borderBottom="1px solid gray"
            width="100%"
            color={colors.messageText.get(category)}
            backgroundColor={colors.messageBackground.get(category)}
          >
            <Box
              display={bodyDirection}
              flexWrap="wrap"
              justifyContent="space-around"
              gridTemplateColumns="repeat(3, 1fr)"
              fontWeight="semibold"
              fontSize={sizes.fontSize.listMessageEmphasis}
            >
              <Text
                textAlign={{ base: 'left', lg: 'center' }}
                textDecoration={
                  isDeadline && canClick ? 'underline' : undefined
                }
                color={canClick ? 'blue' : undefined}
              >
                {isDeadline ? DeadlineUtils.getName(message) : '　'}
              </Text>
              <Text textAlign="center">
                {/* {format(new Date(info.array[0].date), 'MM/dd (ccccc)', {
                    locale: ja,
                  })} */}
                {isDeadline
                  ? DeadlineUtils.isDaily(message)
                    ? Formatter.date(message.date)
                    : DeadlineUtils.isMonthly(message)
                    ? `${message.year}/${message.month
                        .toString()
                        .padStart(2, '0')}${
                        TypeUtils.isNil(message.half)
                          ? ''
                          : ` (${
                              message.half === HalfPeriod.FirstHalf
                                ? '前半'
                                : '後半'
                            })`
                      }`
                    : '　'
                  : Formatter.date(message.dateFrom)}
              </Text>
              <Text textAlign={{ base: 'right', lg: 'center' }}>
                {isDeadline ? message.matterName : '　'}
              </Text>
            </Box>
            <Text fontSize={sizes.fontSize.listMessage}>
              {isDeadline ? message.message : (message as Notice).subject}
            </Text>
            <Divider
              hidden={!isDeadline}
              margin={1}
              borderColor={colors.messageDivider.get(category)}
            />
            {isDeadline
              ? message.ToDoWarnings?.map((deadline, idx) => (
                  <DeadlineItemLine deadline={deadline} key={idx} />
                ))
              : []}
            {/* <Text fontSize={sizes.fontSize.listMessage}>
                {element.content}
              </Text> */}
          </Box>
        </ListItem>
        <Divider h={5} />
      </>
    );
  }
);

const DeadlineItemLine = React.memo(
  ({ deadline, key }: { deadline: ToDoWarning; key: React.Key }) => {
    const sizes = useSizeContext();

    return (
      <HStack key={key} fontSize={sizes.fontSize.listMessage} marginLeft={1}>
        <Text>{Formatter.dateTimeOfWeekString(deadline.limitTime)} 発行</Text>
        <Text>
          {deadline.category === DeadlineCategory.Notice
            ? 'お知らせ'
            : deadline.category === DeadlineCategory.Reminder
            ? 'やること'
            : deadline.category === DeadlineCategory.Warning
            ? '警告'
            : '遅延'}
        </Text>
      </HStack>
    );
  }
);

export default StyledMessageList;
